import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AuthBrandHeading } from '@/auth/auth-brand-heading';
import { Label } from '@/components/ui/label';
import { useAuthStore } from '@/auth/auth-store';
import { forgotPassword } from '@/auth/auth-api';
import { isBlank, isFlutterEmail } from '@/auth/email-validation';
import { safeLogAuthError } from '@/auth/log-helpers';
import { readReturnTo } from '@/auth/return-to';
import { track } from '@/shared/lib/analytics';

/**
 * Forgot Password page (Phase 2 of the auth + PM-login un-defer, PROD-889).
 *
 * Wire-shape parity with Flutter `forgot_password/`:
 *   - `forgot_password_screen.dart` — screen scaffolding + auto-navigate to
 *     access-code-entry on success (`_goNext` at line 36-49)
 *   - `body_widgets.dart` — email field with isEmail validator
 *   - `header_widgets.dart` — "Reset your password" / "Enter your
 *     registration email to receive a reset code"
 *   - `footer_widgets.dart` — "Reset your password" submit + "Log in" back link
 *   - `forgot_password_data_source_impl.dart:22-39` — `POST /api/Account/
 *     ForgotPassword` body `{email}` (already wired via `forgotPassword`)
 *
 * **Audit-drift from Flutter** (documented in CONVERSION_NOTES §0):
 * Flutter has no standalone "Check your email" success screen — it
 * `Navigator.pushReplacementNamed`s straight to the access-code-entry
 * screen (Phase 3a). For Phase 2 standalone, this page shows an inline
 * success state with a "Continue" button that navigates to
 * `/reset-password-access-code` (Phase 3a will land that route).
 */
export function ForgotPasswordPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const status = useAuthStore((s) => s.status);

  const [email, setEmail] = useState('');
  const [phase, setPhase] = useState<'form' | 'sent'>('form');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Already signed in — no need for the forgot-password flow. Mirrors
  // Flutter's pre-login surface gating.
  if (status === 'authenticated' || status === 'refreshing') {
    return <Navigate to={readReturnTo(location.state)} replace />;
  }

  // Mirrors Flutter `body_widgets.dart:29-43` — invalid email clears the
  // mirror used by the button-enable check while the displayed text remains.
  const showEmailError = !isBlank(email) && !isFlutterEmail(email);
  const canSubmit = isFlutterEmail(email) && !isSubmitting;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      await forgotPassword(email);
      // Flutter `forgot_password_bloc.dart:38-43`: fire on success.
      track('Password Reset Initiated', { email });
      setPhase('sent');
    } catch (err) {
      // Flutter `forgot_password_bloc.dart:24-34`: fire on failure (verbatim
      // event name + message + error props).
      track('Password Reset Failed', {
        email,
        message: 'Reset Password Access Failed to send Verification Code',
        error: err instanceof Error ? err.message : String(err),
      });
      // Flutter `forgot_password_data_source_impl.dart:36` throws
      // `Failure('Failed to send Reset code')` on timeout; the repository's
      // standard `handleFailure` covers other DioExceptions. We surface the
      // verbatim Flutter timeout-string for every failure — the backend
      // never returns a useful 4xx body on this endpoint, and "the reset
      // code didn't go" is the only thing the user can act on.
      safeLogAuthError('Forgot password', err);
      toast.error('Failed to send Reset code');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-sidebar px-4">
      <div className="w-90 max-w-full rounded-lg border border-border-default bg-surface-card p-8 flex flex-col gap-4">
        <AuthBrandHeading
          title={phase === 'form' ? 'Reset your password' : 'Check your email'}
          subtitle={phase === 'form'
                ? 'Enter your registration email to receive a reset code'
                : `We've sent a reset code to ${email}. Enter it on the next screen to reset your password.`}
        />

        {phase === 'form' ? (
          <form
            onSubmit={onSubmit}
            className="flex flex-col gap-4"
            aria-label="Reset your password"
          >
            <div className="flex flex-col gap-2">
              <Label
                htmlFor="forgot-password-email"
                className="text-xs font-medium text-text-secondary"
              >
                Email address
              </Label>
              <Input
                id="forgot-password-email"
                type="text"
                inputMode="email"
                autoComplete="username"
                placeholder="Enter email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={showEmailError || undefined}
                aria-describedby={
                  showEmailError ? 'forgot-password-email-error' : undefined
                }
                disabled={isSubmitting}
              />
              {showEmailError && (
                <span
                  id="forgot-password-email-error"
                  role="alert"
                  className="text-sm text-status-error"
                >
                  Email is not valid
                </span>
              )}
            </div>

            <Button type="submit" disabled={!canSubmit} className="mt-2">
              {isSubmitting ? 'Sending…' : 'Reset your password'}
            </Button>

            <Link
              to="/login"
              className="self-center text-sm font-medium text-brand-primary hover:underline"
            >
              Log in
            </Link>
          </form>
        ) : (
          <div className="flex flex-col gap-3">
            <Button
              type="button"
              onClick={() =>
                navigate('/reset-password-access-code', {
                  state: { email },
                })
              }
              className="mt-2"
            >
              Enter access code
            </Button>
            <Link
              to="/login"
              className="self-center text-sm font-medium text-brand-primary hover:underline"
            >
              Back to login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
